import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Combobox from "../Combobox";
import type { ComboboxOption } from "../Combobox/types";
import Icon from "../Icon/Icon";
import { HStack } from "../Layout";
import { Button } from "../ui/button";

// 代表して BusinessPlaceTypeSelect のレスポンス型を参照。
export type DivisionDto = ApiResponseBody<
  "/v2/components/business-place-type-select",
  "get"
>;

type Props = {
  value?: string;
  /** 初期表示用のラベル。可能であれば指定したほうが表示がスムーズです。 */
  displayValue?: string;
  onChange: (option?: ComboboxOption) => void;
  queryKey: string;
  fetchOptions?: () => Promise<DivisionDto>;
  allowEmpty?: boolean;
  hideSearch?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  /** 指定しない場合自律制御 */
  open?: boolean;
  setOpen?: (open: boolean) => void;
};

/**
 * Division の選択肢に対応した Select
 * BusinessPlaceTypeSelect のように DivisionListDto.Division が返るならばどの API でも活用できる。
 * テキスト検索によってオンメモリで絞り込む挙動をとる。
 */
export function DivisionSelect(props: Props) {
  const tc = useCommonTranslation();

  const [_open, _setOpen] = useState(false);
  const open = props.open ?? _open;
  const setOpen = props.setOpen ?? _setOpen;

  const [searchText, setSearchText] = useState("");

  const valueToLabelCache = useValueToLabelCache();

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["division-select", props.queryKey],
    queryFn: async () => props.fetchOptions?.(),
  });

  const filteredOptions = useMemo(() => {
    return data?.items
      .filter((option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase()),
      )
      .map(
        (item) =>
          ({
            value: item.id,
            label: item.label,
          }) satisfies ComboboxOption,
      );
  }, [data, searchText]);

  const isLoading = data == null;

  const label = useMemo(() => {
    const itemLabel = data?.items?.find(
      (option) => option.id === props.value,
    )?.label;
    const cache = valueToLabelCache.get(props.value ?? "");
    return itemLabel || cache || props.displayValue;
  }, [data, props.displayValue, props.value, valueToLabelCache]);

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
        if (!open) setSearchText("");
      }}
    >
      <HStack>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "justify-between overflow-hidden text-ellipsis",
              "min-w-[180px]",
              props.width && `w-[${props.width}px]`,
              !label && "text-muted-foreground",
            )}
            disabled={props.disabled}
          >
            {label || (isLoading ? "" : tc("pleaseSelect"))}
            <Icon
              iconName="ChevronDownIcon"
              className="ml-2 shrink-0 opacity-50"
            />
          </Button>
        </PopoverTrigger>
        {props.allowEmpty && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => props.onChange(undefined)}
          >
            <Icon iconName="XIcon" />
          </Button>
        )}
      </HStack>
      <PopoverContent
        className={cn(
          "w-full overflow-hidden p-0",
          props.width && `w-[${props.width}px]`,
        )}
        align="start"
      >
        <Combobox
          type="single"
          value={props.value ?? null}
          selectionDisplayStyle="hidden"
          onValueChange={(value) => {
            const option = filteredOptions?.find(
              (option) => option.value === value,
            );
            if (option == null) {
              return; // 値クリアの挙動はここでは行わない
            }
            props.onChange(option);
            setSearchText("");
            setOpen(false);
          }}
          isPending={isPending}
          isFetching={isFetching}
          options={filteredOptions ?? []}
          onSearchChange={setSearchText}
          valueToLabelCache={valueToLabelCache}
          hideSearch={props.hideSearch}
        />
      </PopoverContent>
    </Popover>
  );
}
